/**
 * EventFlow - AI-Native Event Lifecycle Suite
 * Main App Entry Point
 *
 * Routes extracted to /routes/AppRoutes.jsx
 * This file handles: QueryClient, AppConfigProvider, BrowserRouter, PWA utilities.
 */
import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Toaster } from "sonner";
import api from "@/lib/api";
import { loadRoleTiers } from "@/lib/roleTier";
import { installAccessRequestPrompt } from "@/lib/accessRequests";
import { authHeaders, getToken } from "@/lib/authToken";

// Routes
import AppRoutes from "@/routes/AppRoutes";
import CookieConsent from "@/components/consent/CookieConsent";
import { ViewAsBanner } from "@/components/admin/ViewAs";
import ConsentGate from "@/components/consent/ConsentGate";

// PWA / Global Components
import OfflineIndicator from "@/components/OfflineIndicator";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import UpdateNotification from "@/components/UpdateNotification";
import CopilotDrawer from "@/components/copilot/CopilotDrawer";
import LabelBatchTray from "@/components/LabelBatchTray";
import AppErrorBoundary from "@/components/AppErrorBoundary";
import MobileTouchUnlock from "@/components/MobileTouchUnlock";
import GlobalCommandPalette from "@/components/GlobalCommandPalette";

// i18n bootstrap lives at @/i18n (imported by src/index.js before App). No
// extra import needed here — kept this comment so future grepping finds it.

// Per-context (event/school/church) favicon + branding swap
import useDynamicBranding from "@/lib/dynamicBranding";
// Google Translate auto-apply on app boot (so dynamic content like exam
// questions / module pages translates without forcing a reload).
import { setGoogleTranslateLanguage, getCurrentGoogleTranslateLang } from "@/lib/googleTranslate";
import i18n from "@/i18n";

const DynamicBrandingProbe = () => {
  useDynamicBranding();
  return null;
};

// Pulls the canonical role→tier map (core/role_registry.py) into the client so
// a tier check here can never disagree with the server — iter 569.
const RoleRegistryBootstrap = () => {
  useEffect(() => {
    installAccessRequestPrompt();
    if (!getToken()) return;
    loadRoleTiers(process.env.REACT_APP_BACKEND_URL, authHeaders());
  }, []);
  return null;
};

// Mirrors the user's selected portal language to Google Translate once the
// widget is ready. Runs once at app boot.
const GoogleTranslateBootstrap = () => {
  useEffect(() => {
    const apply = () => {
      const saved = getCurrentGoogleTranslateLang();
      const portal = i18n.resolvedLanguage || i18n.language || 'en';
      const target = saved && saved !== 'en' ? saved : portal;
      if (target && target !== 'en') {
        setGoogleTranslateLanguage(target);
      }
    };
    if (window.__googleTranslateReady) {
      apply();
    } else {
      window.addEventListener('google-translate-ready', apply, { once: true });
      // Safety net: try after 3s even if the event didn't fire (e.g. cached load)
      setTimeout(apply, 3000);
    }
  }, []);
  return null;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000,
      retry: 1,
    },
  },
});

// ============== App Config Provider ==============
// Handles dynamic favicon, page title, and OG image from admin settings.
const AppConfigProvider = ({ children }) => {
  const { data: appConfig } = useQuery({
    queryKey: ["app-config"],
    queryFn: async () => {
      const response = await api.get('/app-config');
      return response.data;
    },
    staleTime: 60000 * 5,
  });

  useEffect(() => {
    if (appConfig) {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';

      const getFullUrl = (url) => {
        if (!url) return null;
        if (url.startsWith('http')) return url;
        if (url.startsWith('/api/')) return `${API_URL}${url}`;
        return url;
      };

      if (appConfig.app_name) {
        document.title = appConfig.app_name;
      }

      // Site accent colour — Admin → Site Appearance → Brand. Overrides the
      // shipped gold in index.css for every page, public and admin.
      if (appConfig.accent_color) {
        const root = document.documentElement;
        root.style.setProperty('--fwm-accent', appConfig.accent_color);
        if (appConfig.accent_dark) root.style.setProperty('--fwm-accent-dark', appConfig.accent_dark);
        if (appConfig.accent_soft) root.style.setProperty('--fwm-accent-soft', appConfig.accent_soft);
      }

      if (appConfig.favicon_url) {
        const faviconUrl = getFullUrl(appConfig.favicon_url);
        const existingLinks = document.querySelectorAll("link[rel*='icon']");
        existingLinks.forEach(link => {
          if (link.id !== 'dynamic-favicon') link.remove();
        });

        let link = document.getElementById('dynamic-favicon');
        if (!link) {
          link = document.createElement('link');
          link.id = 'dynamic-favicon';
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = faviconUrl;
        link.type = faviconUrl.endsWith('.ico') ? 'image/x-icon' : 'image/png';

        let appleTouchIcon = document.querySelector("link[rel='apple-touch-icon']");
        if (appleTouchIcon) {
          appleTouchIcon.href = faviconUrl;
        }
      }

      if (appConfig.logo_url) {
        const logoUrl = getFullUrl(appConfig.logo_url);
        let ogImage = document.querySelector("meta[property='og:image']");
        if (!ogImage) {
          ogImage = document.createElement('meta');
          ogImage.setAttribute('property', 'og:image');
          document.head.appendChild(ogImage);
        }
        ogImage.setAttribute('content', logoUrl);
      }
    }
  }, [appConfig]);

  return children;
};

// ============== Main App ==============

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppConfigProvider>
        <div className="App">
          <BrowserRouter>
            <DynamicBrandingProbe />
            <RoleRegistryBootstrap />
            <GoogleTranslateBootstrap />
            <MobileTouchUnlock />
            <AppErrorBoundary>
              <ViewAsBanner />
              <ConsentGate>
                <AppRoutes />
              </ConsentGate>
            </AppErrorBoundary>
            <GlobalCommandPalette />
            <CookieConsent />
          </BrowserRouter>
          <OfflineIndicator />
          <PWAInstallPrompt />
          <UpdateNotification />
          <CopilotDrawer />
          <LabelBatchTray />
          <Toaster position="top-right" richColors mobileOffset={{ top: 72 }} />
        </div>
      </AppConfigProvider>
    </QueryClientProvider>
  );
}

export default App;
